import { format } from "date-fns";
import { de, enUS, es, fr, it, pt, zhCN } from "date-fns/locale";
import i18next from "i18next";

import { Language } from "../enums";

/**
 * Gets the appropriate date-fns locale based on the current language
 */
function getDateLocale() {
  const currentLang = i18next.language as Language;
  switch (currentLang) {
    case Language.German:
      return de;
    case Language.French:
      return fr;
    case Language.Spanish:
      return es;
    case Language.Italian:
      return it;
    case Language.Portuguese:
      return pt;
    case Language.Chinese:
      return zhCN;
    case Language.English:
    default:
      return enUS;
  }
}

/**
 * Formats a timezone string to a more readable format
 * @param timezone - The timezone string to format
 * @returns A formatted timezone string e.g (GMT+5) Pakistan Standard Time - Karachi
 */
export function formatCompleteTimezone(timezone: string) {
  try {
    const date = new Date();
    const currentLang = i18next.language;

    // Get timezone name in the current language
    const timeZoneName = Intl.DateTimeFormat(currentLang, {
      timeZone: timezone,
      timeZoneName: "long",
    })
      .formatToParts(date)
      .find(part => part.type === "timeZoneName")
      ?.value || "";

    // Get GMT offset in the current language
    const offset = date.toLocaleString(currentLang, {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).split("GMT")[1];

    // Get city name
    const cityName = timezone.split("/").pop()?.replace("_", " ");

    return `(GMT${offset}) ${timeZoneName} - ${cityName}`;
  }
  catch (error) {
    console.warn(error);
    return timezone;
  }
}

export function formatTimezone(timezone: string) {
  try {
    const date = new Date();
    const currentLang = i18next.language;

    const timeZoneName = Intl.DateTimeFormat(currentLang, {
      timeZone: timezone,
      timeZoneName: "long",
    })
      .formatToParts(date)
      .find(part => part.type === "timeZoneName")
      ?.value || "";

    return timeZoneName;
  }
  catch (error) {
    console.warn(error);
    return timezone;
  }
}

/**
 * Gets the appropriate date format pattern based on the current language
 */
function getDateFormatPattern() {
  const currentLang = i18next.language as Language;
  switch (currentLang) {
    case Language.German:
      return "EEEE, d. MMMM yyyy"; // Montag, 1. April 2026
    case Language.French:
      return "EEEE d MMMM yyyy"; // mercredi 1 avril 2026
    case Language.Spanish:
      return "EEEE, d 'de' MMMM 'de' yyyy"; // miércoles, 1 de abril de 2026
    case Language.Italian:
      return "EEEE d MMMM yyyy"; // mercoledì 1 aprile 2026
    case Language.Portuguese:
      return "EEEE, d 'de' MMMM 'de' yyyy"; // quarta-feira, 1 de abril de 2026
    case Language.Chinese:
      return "yyyy'年'M'月'd'日' EEEE"; // 2026年4月1日 星期三
    case Language.English:
    default:
      return "EEEE, MMMM d, yyyy"; // Monday, April 1, 2026
  }
}

/**
 * Formats a date string to a more readable format
 * @param date - The date string to format (in yyyy-MM-dd format)
 * @param timezone - The timezone to format the date in
 * @returns A formatted date string e.g (Monday, April 10, 2024)
 */
export function formatDate(date: string, timezone?: string) {
  // Parse the date string as a local date (yyyy-MM-dd format)
  const [year, month, day] = date.split("-").map(Number);
  const localDate = new Date(year, month - 1, day);

  const formatPattern = getDateFormatPattern();

  if (timezone) {
    // Convert to the specified timezone
    const timezoneDate = new Date(localDate.toLocaleString("en-US", { timeZone: timezone }));
    return format(timezoneDate, formatPattern, {
      locale: getDateLocale(),
    });
  }

  // Fallback to original behavior if no timezone provided
  return format(localDate, formatPattern, {
    locale: getDateLocale(),
  });
}
